import { Link, Navigate, useSearchParams } from "react-router-dom";
import { Brand } from "../components/layout";
import { Button } from "../components/ui";
import { Seo } from "../lib/seo";
import { NotConfiguredState } from "../components/states";
import { api, apiConfigured } from "../lib/api";
import { useSession } from "../hooks/useSession";
import { demoMode } from "../lib/config";

/**
 * Where to send the customer after a successful sign-in.
 *
 * `?next=` is set by the route guard when someone lands on a protected URL while
 * signed out, so they resume exactly where they were headed. It is accepted only
 * when it is a relative in-app path — never a full URL, protocol-relative path,
 * or anything outside `/app` — so it cannot be used as an open redirect.
 */
export function safeNext(raw: string | null): string {
  if (!raw) return "/app/onboarding";
  if (!raw.startsWith("/app")) return "/app/onboarding";
  if (raw.startsWith("//") || raw.includes("://") || raw.includes("\\")) return "/app/onboarding";
  return raw;
}

/**
 * Sign-in page. The Discord button navigates to the control API's server-side
 * OAuth start endpoint; the browser never handles OAuth tokens.
 */
export function Login(): JSX.Element {
  const [params] = useSearchParams();
  const { state } = useSession();
  const oauthError = params.get("error");
  const next = safeNext(params.get("next"));
  // Show the Google button only when the build enables it (server also 503s if
  // Google isn't configured, so this just avoids a dead control).
  const googleEnabled = (import.meta.env.VITE_GOOGLE_AUTH_ENABLED as string | undefined) === "true";

  const signIn = () => {
    window.location.href = api.loginUrl(next);
  };
  const signInGoogle = () => {
    window.location.href = api.googleLoginUrl(next);
  };

  // Already signed in: don't show a sign-in page, resume the journey.
  if (state.kind === "authenticated") return <Navigate to={next} replace />;

  return (
    <div className="landing">
      <Seo title="Sign in" noindex />
      <header className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
        <Link to="/">
          <Brand />
        </Link>
      </header>

      <section style={{ marginTop: "2rem", maxWidth: "560px" }}>
        <h1>Sign in to ODIN</h1>
        {oauthError === "link_required" ? (
          <p role="alert" style={{ color: "var(--text-secondary)" }}>
            An ODIN account already uses that email address. For your security we don’t merge
            accounts automatically — sign in with your existing method first, then link Google
            under Settings → Linked accounts.
          </p>
        ) : oauthError ? (
          <p role="alert" style={{ color: "var(--color-danger)" }}>
            Sign-in didn’t complete. Please try again.
          </p>
        ) : null}

        {!apiConfigured ? (
          <NotConfiguredState title="The dashboard isn’t connected to the API yet">
            Set <code>VITE_CONTROL_API_URL</code> when building the dashboard to enable Discord
            sign-in.
          </NotConfiguredState>
        ) : (
          <>
            <p>
              ODIN uses your Discord account only to identify you and to see which servers you can
              manage. ODIN adds its own managed all-in-one bot to your server for you — you never
              create or handle a bot token.
            </p>
            <p className="mkt-note" style={{ color: "var(--text-secondary)", fontSize: "0.9rem" }}>
              You must meet Discord’s minimum age requirement to use ODIN with Discord. If you’re
              younger, a parent, guardian, teacher, or community leader needs to create and manage the
              account with you — see the <Link to="/guardians">guardian &amp; younger-user guide</Link>.
            </p>
            <div className="row" style={{ marginTop: "1rem", flexWrap: "wrap", gap: "0.75rem" }}>
              <Button variant="primary" onClick={signIn}>
                Continue with Discord
              </Button>
              {googleEnabled ? (
                <Button variant="secondary" onClick={signInGoogle}>
                  Continue with Google
                </Button>
              ) : null}
              {demoMode ? (
                <Link to="/app">
                  <Button variant="ghost">Explore the dashboard</Button>
                </Link>
              ) : null}
            </div>
            <p className="mkt-note" style={{ color: "var(--text-secondary)", fontSize: "0.85rem", marginTop: "0.75rem" }}>
              Google sign-in lets you keep access even if you lose your Discord account. You can
              connect Discord later, when you install ODIN in a server.
            </p>
          </>
        )}
      </section>
    </div>
  );
}
